<template>
    <RegularModal ref="modal">
        <template v-slot:title>
            <span>{{ translate("federation.create_federation") }}</span>
        </template>

        <template v-slot:body>
            <Notification v-if="federationStore.isFreshOrganizer" class="is-warning">
                <span class="icon">
                    <i class="fas fa-exclamation-triangle"></i>
                </span>
                <span>
                    {{ translate("federation.cannot_create_new_until_approved") }}
                </span>
            </Notification>
            <template v-else>
                <!-- Step 1: Basic info -->
                <template v-if="step === 1">
                    <div class="field">
                        <label class="label">{{ translate("federation.name") }}</label>
                        <div class="control">
                            <input
                                max="50"
                                class="input"
                                type="text"
                                v-model="name"
                                :placeholder="translate('federation.enter_federation_name')"
                            />
                        </div>
                    </div>

                    <div class="field">
                        <label class="label"> {{ translate("manage_academy.country") }} </label>
                        <CountrySelect class="country-select" v-model="country" :dropdown-mode="'container'" />
                    </div>
                </template>

                <!-- Step 2: Billing details -->
                <template v-if="step === 2">
                    <h4 class="title is-5 mb-4">{{ translate("federation.billing_details") }}</h4>
                    <BillingDetailsForm v-model="billingDetails" :validation-errors="billingValidationErrors" />
                </template>
            </template>
        </template>

        <template v-slot:footer>
            <div class="buttons">
                <button class="button" @click="handleBack">
                    <span class="icon">
                        <i class="fas fa-times"></i>
                    </span>
                    <span>{{ resolveFooterButtonLabel }}</span>
                </button>
                <template v-if="!federationStore.isFreshOrganizer">
                    <button v-if="step === 1" class="button is-primary" @click="nextStep" :disabled="!canSubmitStep1">
                        <span>{{ translate("common.next") }}</span>
                        <span class="icon">
                            <i class="fas fa-arrow-right"></i>
                        </span>
                    </button>
                    <LoadingButton
                        v-if="step === 2"
                        class="button is-primary"
                        @click="confirm"
                        :is-loading="confirmInProgress"
                        :disabled="!canSubmitStep2"
                    >
                        <span class="icon">
                            <i class="fas fa-check"></i>
                        </span>
                        <span>{{ translate("common.save") }}</span>
                    </LoadingButton>
                </template>
            </div>
        </template>
    </RegularModal>
</template>

<script setup lang="ts">
    import RegularModal from "../../Common/RegularModal.vue";
    import { useModal } from "../../Common/useModal";
    import federationsApi, { BillingDetails } from "../../Api/federationsApi";
    import { computed, ref } from "vue";
    import LoadingButton from "../../Common/LoadingButton.vue";
    import { useFederationStore } from "./federationStore";
    import CountrySelect from "../../Common/CountrySelect.vue";
    import { translate } from "../../Common/translations";
    import urls from "../../Urls/urls";
    import { handleError } from "../../Common/errorHandler";
    import { successToast } from "../../Common/toasts";
    import Notification from "../../Common/Notification.vue";
    import BillingDetailsForm from "../../Common/BillingDetailsForm.vue";
    import { ValidationError, ValidationErrorsMap } from "../../Common/types";
    import HttpError from "../../Api/Utils/HttpError";

    const federationStore = useFederationStore();

    const { modal, open: openModal, close } = useModal();

    const step = ref(1);
    const name = ref("");
    const country = ref<string | null>(null);
    const billingDetails = ref<BillingDetails>({
        companyName: null,
        street: null,
        city: null,
        postalCode: null,
        country: null,
        taxId: null,
    });
    const billingValidationErrors = ref<ValidationErrorsMap>({});

    function open() {
        step.value = 1;
        name.value = "";
        country.value = null;
        billingDetails.value = {
            companyName: null,
            street: null,
            city: null,
            postalCode: null,
            country: null,
            taxId: null,
        };
        billingValidationErrors.value = {};
        openModal();
    }

    defineExpose({ open });

    const confirmInProgress = ref(false);

    const canSubmitStep1 = computed(() => {
        return name.value.trim() !== "" && country.value !== null;
    });

    const resolveFooterButtonLabel = computed(() => {
        return federationStore.isFreshOrganizer
            ? translate("common.close")
            : step.value === 1
              ? translate("common.cancel")
              : translate("common.back");
    });

    const canSubmitStep2 = computed(() => {
        const b = billingDetails.value;
        return !!(b.companyName?.trim() && b.street?.trim() && b.city?.trim() && b.postalCode?.trim() && b.country && b.taxId?.trim());
    });

    function handleBack() {
        if (step.value > 1) {
            step.value--;
        } else {
            close();
        }
    }

    function nextStep() {
        if (!canSubmitStep1.value) {
            return;
        }
        billingDetails.value.country = country.value;
        step.value = 2;
    }

    function confirm() {
        if (!canSubmitStep2.value) {
            return;
        }
        if (!country.value) {
            return;
        }

        confirmInProgress.value = true;
        billingValidationErrors.value = {};

        return federationsApi
            .createNewFederation({
                name: name.value.trim(),
                country: country.value,
                billingDetails: billingDetails.value,
            })
            .then(response => {
                successToast(translate("federation.created_successfully"));
                window.location.href = urls.federations().manageFederation(response.federationId);
            })
            .catch((err: HttpError) => {
                if (err.validationErrors) {
                    const errors: ValidationErrorsMap = {};
                    err.validationErrors.forEach((e: ValidationError) => {
                        errors[e.field] = e.message;
                    });
                    billingValidationErrors.value = errors;
                    return;
                }
                handleError(err);
            })
            .finally(() => {
                confirmInProgress.value = false;
            });
    }
</script>

<style scoped></style>
